import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import { useUser } from '@tyro/api';
import { Avatar } from '@tyro/core/avatar';
import { useResponsive } from '@tyro/core/hooks';
import { useSaveBarPresence } from '@tyro/core/save-bar';
import { Scrollbar } from '@tyro/core/scrollbar';
import { useLocation } from '@tyro/router/hooks';
import { useEffect } from 'react';
import type { NavigationConfig } from '../../../hooks/use-navigation-config';
import { Logo } from '../../logo';
import { NAV } from './config';
import { ExpandButton } from './expanding-button';
import { NavSectionVertical } from './nav-section';
import { TyroneNavButton } from './TyroneNavButton';

type NavVerticalProps = {
  navConfig: NavigationConfig;
  openNav: boolean;
  onCloseNav: VoidFunction;
  onCollapse: VoidFunction;
};

export default function NavVertical({
  navConfig,
  openNav,
  onCloseNav,
  onCollapse,
}: NavVerticalProps) {
  const { pathname } = useLocation();
  const { activeProfile } = useUser();
  const { saveBarHeight } = useSaveBarPresence();

  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <>
      {isDesktop && <ExpandButton isExpanded onClick={onCollapse} />}
      <Scrollbar
        id="nav-vertical-scrollbar"
        sx={{
          height: 1,
          '& .simplebar-content': {
            height: 1,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack
          spacing={3}
          sx={{
            pt: 3,
            pb: activeProfile?.tenant?.imgUrl ? 1 : 2,
            px: 2.5,
            flexShrink: 0,
          }}
        >
          {activeProfile?.tenant?.imgUrl ? (
            <Avatar
              name={activeProfile.tenant?.name}
              src={activeProfile.tenant.imgUrl}
              size={78}
            />
          ) : (
            <Logo />
          )}
        </Stack>

        <TyroneNavButton />

        <NavSectionVertical
          data={navConfig}
          sx={{ pb: saveBarHeight > 0 ? `${saveBarHeight + 12}px` : 1.5 }}
        />
      </Scrollbar>
    </>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          slotProps={{
            paper: {
              sx: {
                width: NAV.W_DASHBOARD,
                borderRightStyle: 'solid',
                backgroundColor: 'slate.50',
              },
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          slotProps={{
            paper: {
              sx: {
                width: NAV.W_DASHBOARD,
              },
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
